/** Two-icon pill toggle for switching between card and table view modes. */
import {
  ToggleButtonGroup as AriaToggleButtonGroup,
  ToggleButton as AriaToggleButton,
} from 'react-aria-components';
import { motion } from 'motion/react';
import { LayoutGrid, List } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

export type ViewMode = 'card' | 'table';

interface ViewToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  className?: string;
}

/** Button width (h-7 = 28px) + gap (gap-0.5 = 2px) */
const SLIDE_X = 30;

export function ViewToggle({ value, onChange, className }: ViewToggleProps) {
  return (
    <AriaToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={new Set([value])}
      onSelectionChange={(keys) => {
        const selected = [...keys][0] as ViewMode | undefined;
        if (selected) onChange(selected);
      }}
      aria-label="View mode"
      className={cn('relative inline-flex items-center gap-0.5 rounded-full p-1', className)}
      style={{
        background: 'var(--f-bg)',
        border: '1px solid var(--f-border)',
      }}
    >
      {/* Single sliding pill — never unmounts, just translates */}
      <motion.div
        aria-hidden
        className="absolute top-1 left-1 h-7 w-7 rounded-full"
        animate={{ x: value === 'card' ? 0 : SLIDE_X }}
        style={{
          background: 'var(--f-accent-subtle)',
          border: '1px solid rgba(155, 107, 218, 0.15)',
          transform: 'translateZ(0)',
        }}
        transition={{ type: 'spring', stiffness: 500, damping: 35 }}
      />

      <AriaToggleButton
        id="card"
        aria-label="Card view"
        className={({ isFocusVisible }) =>
          cn(
            'relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full outline-none',
            isFocusVisible && 'ring-2 ring-[var(--f-accent)] ring-offset-1'
          )
        }
      >
        <LayoutGrid
          size={14}
          strokeWidth={2}
          className="transition-colors duration-200"
          style={{
            color: value === 'card' ? 'var(--f-accent-text)' : 'var(--f-fg-muted)',
            transform: 'translateZ(0)',
          }}
        />
      </AriaToggleButton>

      <AriaToggleButton
        id="table"
        aria-label="Table view"
        className={({ isFocusVisible }) =>
          cn(
            'relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full outline-none',
            isFocusVisible && 'ring-2 ring-[var(--f-accent)] ring-offset-1'
          )
        }
      >
        <List
          size={14}
          strokeWidth={2}
          className="transition-colors duration-200"
          style={{
            color: value === 'table' ? 'var(--f-accent-text)' : 'var(--f-fg-muted)',
            transform: 'translateZ(0)',
          }}
        />
      </AriaToggleButton>
    </AriaToggleButtonGroup>
  );
}
